import type { EarthlyBranch } from '../types/branches';
import type { Relation } from '../types/relations';
import type { PillarPosition } from '../types/pillars';
import { branchIndex } from '../core/sexagenary';

// 삼형 (3글자 모두) / 상형 (2글자만):
// 지세지형 (恃勢之刑): 寅(2)→巳(5)→申(8)→寅 (circular)
// 무은지형 (無恩之刑): 丑(1)→戌(10)→未(7)→丑 (circular)
// 자묘형 (무례지형/無禮之刑): 子(0)↔卯(3) (mutual)
// 자형 (自刑): 辰辰, 午午, 酉酉, 亥亥

export const TRIPLE_PUNISH_1: number[] = [2, 5, 8];   // 寅巳申
export const TRIPLE_PUNISH_2: number[] = [1, 10, 7];  // 丑戌未
export const MUTUAL_PUNISH: [number, number] = [0, 3]; // 子卯
export const SELF_PUNISH: number[] = [4, 6, 9, 11];   // 辰午酉亥

export function findEarthlyPunishments(
  branches: { branch: EarthlyBranch; position: PillarPosition }[]
): Relation[] {
  const relations: Relation[] = [];
  const items = branches.map(b => ({ idx: branchIndex(b.branch), ...b }));

  // Check triple punishments: 삼형 (3글자 모두) or 상형 (2글자만)
  for (const tripleSet of [TRIPLE_PUNISH_1, TRIPLE_PUNISH_2]) {
    const found = items.filter(b => tripleSet.includes(b.idx));
    if (found.length >= 2) {
      const uniqueIndices = new Set(found.map(f => f.idx));
      const relType = uniqueIndices.size >= 3 ? '삼형' : '상형';
      for (let i = 0; i < found.length; i++) {
        for (let j = i + 1; j < found.length; j++) {
          // 같은 지지끼리는 형 관계가 아님 (예: 丑-丑)
          if (found[i].idx === found[j].idx) continue;
          // 정식 순서: tripleSet 배열 내 순서를 따름
          const posI = tripleSet.indexOf(found[i].idx);
          const posJ = tripleSet.indexOf(found[j].idx);
          const [first, second] = posI < posJ ? [found[i], found[j]] : [found[j], found[i]];
          relations.push({
            type: relType,
            pillars: [found[i].position, found[j].position],
            branches: [first.branch, second.branch],
            description: `${first.branch}${second.branch}형`,
          });
        }
      }
    }
  }

  // Check mutual punishment 子↔卯
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i].idx;
      const b = items[j].idx;
      if ((a === MUTUAL_PUNISH[0] && b === MUTUAL_PUNISH[1]) ||
          (a === MUTUAL_PUNISH[1] && b === MUTUAL_PUNISH[0])) {
        const [first, second] = a === MUTUAL_PUNISH[0] ? [items[i], items[j]] : [items[j], items[i]];
        relations.push({
          type: '자묘형',
          pillars: [items[i].position, items[j].position],
          branches: [first.branch, second.branch],
          description: `${first.branch}${second.branch}형 (무례지형)`,
        });
      }
    }
  }

  // Check self-punishment (same branch appears twice)
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (items[i].idx === items[j].idx && SELF_PUNISH.includes(items[i].idx)) {
        relations.push({
          type: '자형',
          pillars: [items[i].position, items[j].position],
          branches: [items[i].branch, items[j].branch],
          description: `${items[i].branch}${items[j].branch} 자형`,
        });
      }
    }
  }

  return relations;
}
